import { storage } from "@/lib/local-storage";
import type {
    CreateEmailTemplateData,
    CreatePricingSettingsData,
    CreateQuoteRequestData,
    CreateQuoteRuleData,
    CreateShipperQuoteRequestData,
    DraftingInstruction,
    EmailTemplate,
    PricingSettings,
    ProcessingStep,
    QuoteFilters,
    QuoteRequest,
    QuoteResponse,
    QuoteRule,
    SellRateComponent,
    ShipperQuoteRequest,
    UpdateEmailTemplateData,
    UpdatePricingSettingsData,
    UpdateQuoteRequestData,
    UpdateQuoteRuleData,
    UpdateShipperQuoteRequestData,
} from "@/types/quote";
import { accountService } from "./mock-accounts";
import { addressBookService } from "./mock-address-book";
import { containerService } from "./mock-containers";

const now = new Date();

// Pool di dati per generare quote randomiche
const randomNames = [
    "Marco Bianchi",
    "Sofia Rossi",
    "Luca Verdi",
    "Giulia Ferrari",
    "Andrea Romano",
    "Chiara Esposito",
    "Francesco Ricci",
    "Valentina Marino",
    "Alessandro Greco",
    "Martina Bruno",
    "Davide Costa",
    "Elena Fontana",
    "Matteo Conti",
    "Sara Gallo",
];

const randomEmails = [
    "marco.bianchi@example.com",
    "sofia.rossi@example.com",
    "luca.verdi@example.com",
    "giulia.ferrari@example.com",
    "andrea.romano@example.com",
    "chiara.esposito@example.com",
    "francesco.ricci@example.com",
    "valentina.marino@example.com",
    "alessandro.greco@example.com",
    "martina.bruno@example.com",
    "davide.costa@example.com",
    "elena.fontana@example.com",
];

const randomProducts = [
    "caffè",
    "cioccolato",
    "banane",
    "mango",
    "ananas",
    "cacao",
    "zucchero",
    "riso",
    "quinoa",
    "legumi",
    "spezie",
    "tè",
    "cacao in polvere",
    "frutta secca",
];

const randomOrigins = [
    "San Paolo, Brasile",
    "Bogotà, Colombia",
    "Quito, Ecuador",
    "Lima, Perù",
    "Buenos Aires, Argentina",
    "Santiago, Cile",
    "Montevideo, Uruguay",
    "La Paz, Bolivia",
];

const randomDestinations = [
    "Milano, Italia",
    "Roma, Italia",
    "Napoli, Italia",
    "Torino, Italia",
    "Barcellona, Spagna",
    "Madrid, Spagna",
    "Parigi, Francia",
    "Amsterdam, Paesi Bassi",
];

const randomSubjects = [
    "Richiesta preventivo trasporto",
    "Preventivo urgente",
    "Richiesta quotazione",
    "Preventivo trasporto merci",
    "Richiesta preventivo container",
    "Preventivo trasporto internazionale",
    "Quotazione trasporto",
    "Richiesta preventivo spedizione",
];

const randomBodyTemplates = [
    `Gentile fornitore,

siamo interessati a ricevere un preventivo per il trasporto di {{quantity}} {{unit}} di {{product}}.

Dettagli:
- Origine: {{origin}}
- Destinazione: {{destination}}
- Ritiro: {{pickupDate}}
- Consegna: {{deliveryDate}}
- Temperatura controllata: richiesta

Restiamo in attesa di un vostro preventivo.

Cordiali saluti,
{{name}}`,

    `Buongiorno,

abbiamo bisogno di un preventivo per il trasporto di {{product}}.

Richiediamo:
- Origine: {{origin}}
- Destinazione: {{destination}}
- Quantità: {{quantity}} {{unit}}
- Ritiro: {{pickupDate}}
- Consegna: {{deliveryDate}}

Potete fornirci un preventivo dettagliato?

Grazie,
{{name}}`,

    `Salve,

cerchiamo un preventivo per trasporto {{product}}.

Dettagli:
- Da: {{origin}}
- A: {{destination}}
- Quantità: {{quantity}} {{unit}}
- Data ritiro: {{pickupDate}}
- Data consegna: {{deliveryDate}}

Richiediamo anche certificazioni necessarie.

Cordiali saluti,
{{name}}`,
];

// Step predefiniti per processing
const defaultProcessingSteps: Omit<ProcessingStep, "status">[] = [
    { id: "analyzing", label: "Analisi testo email" },
    { id: "identifying", label: "Identificazione prodotto" },
    { id: "extracting", label: "Estrazione dati strutturati" },
    { id: "calculating", label: "Calcolo costi" },
    { id: "applying", label: "Applicazione regole" },
    { id: "generating", label: "Generazione risposta" },
];

// Funzione per generare una quote request randomica
export const generateRandomQuoteRequest = (): QuoteRequest => {
    const randomName = randomNames[Math.floor(Math.random() * randomNames.length)];
    const randomEmail = randomEmails[Math.floor(Math.random() * randomEmails.length)];
    const randomProduct = randomProducts[Math.floor(Math.random() * randomProducts.length)];
    const randomOrigin = randomOrigins[Math.floor(Math.random() * randomOrigins.length)];
    const randomDestination = randomDestinations[Math.floor(Math.random() * randomDestinations.length)];
    const randomSubject = randomSubjects[Math.floor(Math.random() * randomSubjects.length)];
    const randomBodyTemplate = randomBodyTemplates[Math.floor(Math.random() * randomBodyTemplates.length)];

    const quantity = Math.floor(Math.random() * 1000) + 100;
    const unit = Math.random() > 0.5 ? "kg" : "container";
    const pickupDate = new Date(now.getTime() + Math.random() * 30 * 24 * 60 * 60 * 1000);
    const deliveryDate = new Date(pickupDate.getTime() + Math.random() * 20 * 24 * 60 * 60 * 1000);

    const body = randomBodyTemplate
        .replace("{{name}}", randomName)
        .replace("{{product}}", randomProduct)
        .replace("{{origin}}", randomOrigin)
        .replace("{{destination}}", randomDestination)
        .replace("{{quantity}}", quantity.toString())
        .replace("{{unit}}", unit)
        .replace("{{pickupDate}}", pickupDate.toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }))
        .replace("{{deliveryDate}}", deliveryDate.toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" }));

    const id = `quote-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Inizializza tutti gli step come pending
    const processingSteps: ProcessingStep[] = defaultProcessingSteps.map((step) => ({
        ...step,
        status: "pending" as const,
    }));

    return {
        id,
        from: randomEmail,
        fromName: randomName,
        subject: `${randomSubject} ${randomProduct}`,
        body,
        receivedAt: new Date(),
        status: "pending",
        isProcessing: true,
        processingSteps,
        processingProgress: 0,
        lastUpdated: new Date(),
        extractedData: {
            product: randomProduct,
            origin: randomOrigin,
            destination: randomDestination,
            quantity,
            unit,
            pickupDate,
            deliveryDate,
            estimatedValue: Math.floor(Math.random() * 50000 + 5000),
            currency: "EUR",
        },
    };
};

// Mock data per quote requests
const mockQuoteRequests: QuoteRequest[] = [
    {
        id: "1",
        from: "mario.rossi@example.com",
        fromName: "Mario Rossi",
        subject: "Richiesta preventivo per trasporto avocado",
        body: `Gentile fornitore,

siamo interessati a ricevere un preventivo per il trasporto di 500 kg di avocado dal Perù a Milano.

Richiediamo:
- Ritiro: Lima, Perù - 15 marzo 2024
- Consegna: Milano, Italia - entro il 25 marzo 2024
- Temperatura controllata: 4-8°C
- Certificazioni: necessarie per importazione UE

Restiamo in attesa di un vostro preventivo dettagliato.

Cordiali saluti,
Mario Rossi
Import Export SRL`,
        receivedAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000),
        status: "auto_responded",
        autoResponded: true,
        requiresManualReview: false, // Esplicitamente false per quote auto-responded
        respondedAt: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000),
        responseEmailId: "resp-1",
        responseSubject: "Preventivo trasporto avocado - QUOTE-001",
        responseBody: `Gentile Mario Rossi,

grazie per la sua richiesta di preventivo per il trasporto di avocado.

Siamo lieti di fornirle il seguente preventivo:

**Dettagli Trasporto:**
- Origine: Lima, Perù
- Destinazione: Milano, Italia
- Quantità: 500 kg
- Data ritiro: 15 marzo 2024
- Data consegna prevista: 25 marzo 2024

**Preventivo:**
- Costo base: 2.500 EUR
- Componenti aggiuntive: 500 EUR
- **Totale: 3.000 EUR**

Il preventivo è valido per 30 giorni.

Restiamo a disposizione per qualsiasi chiarimento.

Cordiali saluti,
Il Team Logistico`,
        extractedData: {
            product: "avocado",
            destination: "Milano, Italia",
            origin: "Lima, Perù",
            quantity: 500,
            unit: "kg",
            pickupDate: new Date("2024-03-15"),
            deliveryDate: new Date("2024-03-25"),
            temperatureRequirements: "4-8°C",
            customsRequirements: "Certificazione importazione UE",
            estimatedValue: 5000,
            currency: "EUR",
        },
    },
    {
        id: "2",
        from: "lucia.bianchi@example.com",
        fromName: "Lucia Bianchi",
        subject: "Preventivo trasporto merci deperibili",
        body: `Buongiorno,

abbiamo bisogno di un preventivo urgente per il trasporto di prodotti alimentari surgelati.

Dettagli:
- Origine: Buenos Aires, Argentina
- Destinazione: Roma, Italia
- Quantità: 2 container refrigerati
- Ritiro: 20 marzo 2024
- Consegna: 5 aprile 2024
- Prodotto: Carne bovina surgelata

Potete fornirci un preventivo con tutte le spese incluse?

Grazie,
Lucia Bianchi
Food Import Italia`,
        receivedAt: new Date(now.getTime() - 1 * 24 * 60 * 60 * 1000),
        status: "requires_review",
        autoResponded: false, // Esplicitamente false per quote requires review
        requiresManualReview: true,
        reviewReason: "Destinazione Sud America richiede ricarico speciale",
        extractedData: {
            product: "carne bovina surgelata",
            destination: "Roma, Italia",
            origin: "Buenos Aires, Argentina",
            quantity: 2,
            unit: "container",
            pickupDate: new Date("2024-03-20"),
            deliveryDate: new Date("2024-04-05"),
            temperatureRequirements: "Surgelato (-18°C)",
            equipmentType: "Container refrigerato",
            estimatedValue: 15000,
            currency: "EUR",
        },
    },
    {
        id: "3",
        from: "giovanni.verdi@example.com",
        fromName: "Giovanni Verdi",
        subject: "Richiesta preventivo container",
        body: `Salve,

cerchiamo un preventivo per trasporto container da Shanghai a Genova.

Dettagli:
- Tipo: Container 40 piedi
- Merce: Elettronica
- Ritiro: 10 aprile 2024
- Consegna: 15 maggio 2024
- Valore merce: €50.000

Richiediamo anche assicurazione completa.

Cordiali saluti,
Giovanni Verdi
Tech Import SRL`,
        receivedAt: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000),
        status: "auto_responded",
        autoResponded: true,
        requiresManualReview: false, // Esplicitamente false per quote auto-responded
        respondedAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000),
        responseEmailId: "resp-3",
        responseSubject: "Preventivo trasporto elettronica - QUOTE-003",
        responseBody: `Gentile Giovanni Verdi,

grazie per la sua richiesta di preventivo per il trasporto di elettronica.

Siamo lieti di fornirle il seguente preventivo:

**Dettagli Trasporto:**
- Origine: Shanghai, Cina
- Destinazione: Genova, Italia
- Quantità: 1 container 40 piedi
- Data ritiro: 10 aprile 2024
- Data consegna prevista: 15 maggio 2024

**Preventivo:**
- Costo base: 4.500 EUR
- Componenti aggiuntive: 1.200 EUR
- **Totale: 5.700 EUR**

Il preventivo è valido per 30 giorni.

Restiamo a disposizione per qualsiasi chiarimento.

Cordiali saluti,
Il Team Logistico`,
        extractedData: {
            product: "elettronica",
            destination: "Genova, Italia",
            origin: "Shanghai, Cina",
            quantity: 1,
            unit: "container 40 piedi",
            pickupDate: new Date("2024-04-10"),
            deliveryDate: new Date("2024-05-15"),
            estimatedValue: 50000,
            currency: "EUR",
            equipmentType: "Container 40 piedi",
            specialRequirements: ["Assicurazione completa"],
        },
    },
    {
        id: "4",
        from: "anna.neri@example.com",
        fromName: "Anna Neri",
        subject: "Preventivo urgente",
        body: `Buongiorno,

abbiamo bisogno di un preventivo immediato per trasporto urgente.

Prodotto: Farmaci
Origine: Zurigo, Svizzera
Destinazione: Napoli, Italia
Ritiro: domani (se possibile)
Consegna: entro 48 ore

È possibile?

Grazie,
Anna Neri
Pharma Logistics`,
        receivedAt: new Date(now.getTime() - 6 * 60 * 60 * 1000), // 6 ore fa
        status: "pending",
        autoResponded: false, // Esplicitamente false per quote requires review
        requiresManualReview: true,
        reviewReason: "Ritiro richiesto in weekend",
        extractedData: {
            product: "farmaci",
            destination: "Napoli, Italia",
            origin: "Zurigo, Svizzera",
            pickupDate: new Date(now.getTime() + 24 * 60 * 60 * 1000), // Domani
            deliveryDate: new Date(now.getTime() + 48 * 60 * 60 * 1000), // 48 ore
            specialRequirements: ["Urgente", "Consegna entro 48 ore"],
            estimatedValue: 3000,
            currency: "EUR",
        },
    },
    {
        id: "5",
        from: "paolo.ferrari@example.com",
        fromName: "Paolo Ferrari",
        subject: "Richiesta preventivo trasporto vino",
        body: `Gentile fornitore,

siamo interessati a un preventivo per trasporto di vino pregiato.

Dettagli:
- Prodotto: Vino Chianti DOCG
- Quantità: 1000 bottiglie
- Origine: Firenze, Italia
- Destinazione: New York, USA
- Ritiro: 1 aprile 2024
- Consegna: 20 aprile 2024
- Trattamento: Fragile, temperatura controllata

Restiamo in attesa.

Cordiali saluti,
Paolo Ferrari
Wine Export Italia`,
        receivedAt: new Date(now.getTime() - 4 * 24 * 60 * 60 * 1000),
        status: "responded",
        respondedAt: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000),
        responseEmailId: "resp-5",
        extractedData: {
            product: "vino Chianti DOCG",
            destination: "New York, USA",
            origin: "Firenze, Italia",
            quantity: 1000,
            unit: "bottiglie",
            pickupDate: new Date("2024-04-01"),
            deliveryDate: new Date("2024-04-20"),
            temperatureRequirements: "Controllata",
            specialRequirements: ["Fragile", "Trattamento speciale"],
            estimatedValue: 25000,
            currency: "EUR",
        },
    },
];

// Mock data per quote rules
const mockQuoteRules: QuoteRule[] = [
    {
        id: "rule-1",
        name: "Regola Avocado",
        description: "Classifica richieste per avocado",
        naturalLanguageRule:
            "Identifica richieste di preventivo per trasporto di avocado. Cerca nel testo email parole chiave come 'avocado', 'persea americana', o riferimenti a frutta tropicale verde.",
        autoResponseConditions: ["se il prodotto richiesto è avocado", "se la destinazione è in Europa", "se la temperatura richiesta è tra 4-8°C"],
        isActive: true,
        createdAt: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000),
    },
    {
        id: "rule-2",
        name: "Regola Sud America",
        description: "Applica ricarico per destinazioni Sud America",
        naturalLanguageRule:
            "Identifica richieste con origine o destinazione in Sud America. Cerca riferimenti a paesi come Argentina, Brasile, Cile, Perù, Colombia, ecc.",
        autoResponseConditions: ["se la destinazione è il Sud America applica il 20% di ricarico", "se l'origine è il Sud America applica il 15% di ricarico"],
        isActive: true,
        createdAt: new Date(now.getTime() - 25 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000),
    },
    {
        id: "rule-3",
        name: "Regola Weekend",
        description: "Verifica se ritiro è in weekend",
        naturalLanguageRule: "Identifica se il giorno di ritiro richiesto cade in un weekend (sabato o domenica).",
        autoResponseConditions: ["il giorno di ritiro non è weekend"],
        isActive: true,
        createdAt: new Date(now.getTime() - 20 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000),
    },
];

// Mock data per pricing settings
const mockPricingSettings: PricingSettings[] = [
    {
        id: "pricing-1",
        ruleId: "rule-1",
        buyRate: 2000,
        margin: {
            value: 15,
            type: "percentage",
        },
        sellRateComponents: [
            {
                id: "comp-1",
                name: "Costo carburante",
                value: 500,
                type: "currency",
                description: "Costo stimato carburante per il viaggio",
            },
            {
                id: "comp-2",
                name: "Live loading fee",
                value: 200,
                type: "currency",
                description: "Costo per operazioni di carico",
            },
            {
                id: "comp-3",
                name: "Assicurazione",
                value: 2,
                type: "percentage",
                description: "Percentuale sul valore merce",
            },
        ],
        baseCurrency: "EUR",
        createdAt: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000),
    },
    {
        id: "pricing-2",
        ruleId: "rule-2",
        buyRate: 3000,
        margin: {
            value: 20,
            type: "percentage",
        },
        sellRateComponents: [
            {
                id: "comp-4",
                name: "Costo carburante",
                value: 800,
                type: "currency",
            },
            {
                id: "comp-5",
                name: "Ricarico Sud America",
                value: 20,
                type: "percentage",
                description: "Ricarico applicato per destinazioni Sud America",
            },
        ],
        baseCurrency: "EUR",
        createdAt: new Date(now.getTime() - 25 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000),
    },
];

// Mock data per email templates
const mockEmailTemplates: EmailTemplate[] = [
    {
        id: "template-1",
        name: "Template Avocado",
        ruleId: "rule-1",
        subject: "Preventivo trasporto {{product}} - {{quoteId}}",
        body: `Gentile {{recipientName}},

grazie per la sua richiesta di preventivo per il trasporto di {{product}}.

Siamo lieti di fornirle il seguente preventivo:

**Dettagli Trasporto:**
- Origine: {{origin}}
- Destinazione: {{destination}}
- Quantità: {{quantity}} {{unit}}
- Data ritiro: {{pickupDate}}
- Data consegna prevista: {{deliveryDate}}

**Preventivo:**
- Costo base: {{basePrice}} {{currency}}
- Componenti aggiuntive: {{additionalComponents}}
- **Totale: {{totalPrice}} {{currency}}**

Il preventivo è valido per 30 giorni.

Restiamo a disposizione per qualsiasi chiarimento.

Cordiali saluti,
Il Team Logistico`,
        draftingInstructions: [
            {
                id: "inst-1",
                instruction: "chiama il recipient per nome",
                order: 1,
            },
            {
                id: "inst-2",
                instruction: "arrotonda le cifre ai 5€ più vicini",
                order: 2,
            },
            {
                id: "inst-3",
                instruction: "non specificare il tipo di equipaggiamento usato ma resta generico",
                order: 3,
            },
        ],
        variables: [
            "recipientName",
            "product",
            "origin",
            "destination",
            "quantity",
            "unit",
            "pickupDate",
            "deliveryDate",
            "basePrice",
            "currency",
            "additionalComponents",
            "totalPrice",
            "quoteId",
        ],
        createdAt: new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000),
    },
    {
        id: "template-2",
        name: "Template Generico",
        subject: "Preventivo trasporto - {{quoteId}}",
        body: `Gentile {{recipientName}},

grazie per la sua richiesta di preventivo.

Siamo lieti di fornirle il seguente preventivo:

**Dettagli:**
{{details}}

**Preventivo:**
- Totale: {{totalPrice}} {{currency}}

Il preventivo è valido per 30 giorni.

Cordiali saluti,
Il Team Logistico`,
        draftingInstructions: [
            {
                id: "inst-4",
                instruction: "usa un tono professionale ma amichevole",
                order: 1,
            },
            {
                id: "inst-5",
                instruction: "evidenzia i punti chiave del preventivo",
                order: 2,
            },
        ],
        variables: ["recipientName", "details", "totalPrice", "currency", "quoteId"],
        createdAt: new Date(now.getTime() - 20 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000),
    },
];

// LocalStorage keys
const LOCAL_STORAGE_KEY_QUOTE_REQUESTS = "mockQuoteRequests";
const LOCAL_STORAGE_KEY_SHIPPER_QUOTE_REQUESTS = "mockShipperQuoteRequests";

// Helper per serializzare/deserializzare Date nelle QuoteRequest
function serializeQuoteRequest(quote: QuoteRequest): any {
    return {
        ...quote,
        receivedAt: quote.receivedAt.toISOString(),
        lastUpdated: quote.lastUpdated?.toISOString(),
        respondedAt: quote.respondedAt?.toISOString(),
        extractedData: quote.extractedData
            ? {
                  ...quote.extractedData,
                  pickupDate: quote.extractedData.pickupDate?.toISOString(),
                  deliveryDate: quote.extractedData.deliveryDate?.toISOString(),
              }
            : undefined,
    };
}

function deserializeQuoteRequest(quote: any): QuoteRequest {
    return {
        ...quote,
        receivedAt: new Date(quote.receivedAt),
        lastUpdated: quote.lastUpdated ? new Date(quote.lastUpdated) : undefined,
        respondedAt: quote.respondedAt ? new Date(quote.respondedAt) : undefined,
        extractedData: quote.extractedData
            ? {
                  ...quote.extractedData,
                  pickupDate: quote.extractedData.pickupDate ? new Date(quote.extractedData.pickupDate) : undefined,
                  deliveryDate: quote.extractedData.deliveryDate ? new Date(quote.extractedData.deliveryDate) : undefined,
              }
            : undefined,
    };
}

// Carica quote requests da localStorage o usa i mock iniziali
function loadQuoteRequests(): QuoteRequest[] {
    if (typeof window === "undefined") {
        return [...mockQuoteRequests];
    }

    try {
        const stored = storage.getItem(LOCAL_STORAGE_KEY_QUOTE_REQUESTS);
        if (stored) {
            const parsed = JSON.parse(stored);
            const storedQuotes = parsed.map(deserializeQuoteRequest);
            const mockIds = new Set(mockQuoteRequests.map((q) => q.id));
            const newStoredQuotes = storedQuotes.filter((q: QuoteRequest) => !mockIds.has(q.id));

            // Rimuovi duplicati basati su from, subject e receivedAt (stesso shipper, stesso subject, creato nello stesso momento)
            const uniqueQuotes = new Map<string, QuoteRequest>();
            const allQuotes = [...mockQuoteRequests, ...newStoredQuotes];

            allQuotes.forEach((quote) => {
                // Crea una chiave unica basata su from, subject e data (arrotondata al minuto)
                const dateKey = quote.receivedAt ? new Date(quote.receivedAt.getTime() - (quote.receivedAt.getTime() % 60000)).toISOString() : "";
                const uniqueKey = `${quote.from}|${quote.subject}|${dateKey}`;

                // Mantieni solo la prima occorrenza
                if (!uniqueQuotes.has(uniqueKey)) {
                    uniqueQuotes.set(uniqueKey, quote);
                } else {
                    // Se esiste già, mantieni quella con ID più recente (più recente)
                    const existing = uniqueQuotes.get(uniqueKey)!;
                    if (quote.receivedAt && existing.receivedAt && quote.receivedAt > existing.receivedAt) {
                        uniqueQuotes.set(uniqueKey, quote);
                    }
                }
            });

            return Array.from(uniqueQuotes.values());
        }
    } catch (error) {
        console.error("Error loading quote requests from localStorage:", error);
    }

    return [...mockQuoteRequests];
}

// Salva quote requests in localStorage
function saveQuoteRequests(): void {
    if (typeof window === "undefined") return;
    try {
        storage.setItem(LOCAL_STORAGE_KEY_QUOTE_REQUESTS, JSON.stringify(quoteRequests.map(serializeQuoteRequest)));
    } catch (error) {
        console.error("Error saving quote requests to localStorage:", error);
    }
}

// In-memory storage
let quoteRequests = loadQuoteRequests();
let quoteRules = [...mockQuoteRules];
let pricingSettings = [...mockPricingSettings];
let emailTemplates = [...mockEmailTemplates];

// Quote Request Service
export const quoteService = {
    // Get all quote requests
    getAll: (): QuoteRequest[] => {
        return [...quoteRequests];
    },

    // Get quote request by ID
    getById: (id: string): QuoteRequest | undefined => {
        return quoteRequests.find((quote) => quote.id === id);
    },

    // Create new quote request
    create: (data: CreateQuoteRequestData): QuoteRequest => {
        const newQuote: QuoteRequest = {
            id: (quoteRequests.length + 1).toString(),
            ...data,
            receivedAt: data.receivedAt || new Date(),
            status: "pending",
        };
        quoteRequests.push(newQuote);
        saveQuoteRequests(); // Salva in localStorage
        return newQuote;
    },

    // Add quote request with custom ID (for simulated quotes)
    add: (quote: QuoteRequest): QuoteRequest => {
        // Verifica se esiste già
        const existingIndex = quoteRequests.findIndex((q) => q.id === quote.id);
        if (existingIndex !== -1) {
            // Aggiorna se esiste già
            quoteRequests[existingIndex] = quote;
            saveQuoteRequests(); // Salva in localStorage
            return quote;
        }
        // Aggiungi se non esiste
        quoteRequests.push(quote);
        saveQuoteRequests(); // Salva in localStorage
        return quote;
    },

    // Update quote request
    update: (id: string, data: UpdateQuoteRequestData): QuoteRequest | null => {
        const index = quoteRequests.findIndex((quote) => quote.id === id);
        if (index === -1) return null;

        quoteRequests[index] = {
            ...quoteRequests[index],
            ...data,
        };
        saveQuoteRequests(); // Salva in localStorage
        return quoteRequests[index];
    },

    // Delete quote request
    delete: (id: string): boolean => {
        const index = quoteRequests.findIndex((quote) => quote.id === id);
        if (index === -1) return false;

        quoteRequests.splice(index, 1);
        saveQuoteRequests(); // Salva in localStorage
        return true;
    },

    // Search and filter quote requests
    search: (filters: QuoteFilters): QuoteRequest[] => {
        let filtered = [...quoteRequests];

        if (filters.status) {
            filtered = filtered.filter((quote) => quote.status === filters.status);
        }

        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            filtered = filtered.filter(
                (quote) =>
                    quote.subject.toLowerCase().includes(searchTerm) ||
                    quote.body.toLowerCase().includes(searchTerm) ||
                    quote.from.toLowerCase().includes(searchTerm) ||
                    quote.fromName?.toLowerCase().includes(searchTerm),
            );
        }

        if (filters.from) {
            filtered = filtered.filter((quote) => quote.from === filters.from);
        }

        if (filters.dateFrom) {
            filtered = filtered.filter((quote) => quote.receivedAt >= filters.dateFrom!);
        }

        if (filters.dateTo) {
            filtered = filtered.filter((quote) => quote.receivedAt <= filters.dateTo!);
        }

        if (filters.autoResponded !== undefined) {
            filtered = filtered.filter((quote) => quote.autoResponded === filters.autoResponded);
        }

        if (filters.requiresManualReview !== undefined) {
            filtered = filtered.filter((quote) => quote.requiresManualReview === filters.requiresManualReview);
        }

        return filtered;
    },

    // Marca una quote come con booking email inviata (simulazione)
    markBookingRequested: (id: string): QuoteRequest | null => {
        const index = quoteRequests.findIndex((quote) => quote.id === id);
        if (index === -1) return null;

        quoteRequests[index] = {
            ...quoteRequests[index],
            hasBookingEmail: true,
            lastUpdated: new Date(),
        };

        return quoteRequests[index];
    },

    // Richiedi booking number per una quote accettata (collega QuoteRequest FF con QuoteResponse Shipper)
    requestBookingNumberForQuote: (quoteRequestId: string): QuoteRequest | null => {
        const quoteRequest = quoteRequests.find((qr) => qr.id === quoteRequestId);
        if (!quoteRequest || quoteRequest.shipperStatus !== "accepted") return null;

        // Trova la QuoteResponse corrispondente nello ShipperQuoteRequest
        for (const shipperRequest of shipperQuoteRequests) {
            const matchingResponse = shipperRequest.receivedQuotes.find((qr) => {
                // Match tramite subject o body
                if (quoteRequest.responseSubject && qr.subject) {
                    return quoteRequest.responseSubject === qr.subject;
                }
                if (quoteRequest.responseBody && qr.body) {
                    const qrBodyPreview = quoteRequest.responseBody.substring(0, 100).trim();
                    const responseBodyPreview = qr.body.substring(0, 100).trim();
                    return qrBodyPreview === responseBodyPreview;
                }
                return false;
            });

            if (matchingResponse && matchingResponse.status === "accepted") {
                // Aggiorna la QuoteResponse
                matchingResponse.bookingNumberRequested = true;
                matchingResponse.bookingNumberRequestedAt = new Date();
                shipperRequest.updatedAt = new Date();
                saveShipperQuoteRequests(); // Salva in localStorage
                break;
            }
        }

        quoteRequest.hasBookingEmail = true;
        quoteRequest.lastUpdated = new Date();
        saveQuoteRequests(); // Salva in localStorage
        return quoteRequest;
    },

    // Imposta booking number per una quote (collega QuoteRequest FF con QuoteResponse Shipper)
    setBookingNumberForQuote: (quoteRequestId: string, bookingNumber: string, ffEmail?: string): QuoteRequest | null => {
        const quoteRequest = quoteRequests.find((qr) => qr.id === quoteRequestId);
        if (!quoteRequest) return null;

        let matchingResponse: QuoteResponse | undefined;
        let targetShipperRequest: ShipperQuoteRequest | undefined;

        // Trova la QuoteResponse corrispondente nello ShipperQuoteRequest
        for (const shipperRequest of shipperQuoteRequests) {
            // Prima prova a matchare per responseEmailId se disponibile (più affidabile)
            if (quoteRequest.responseEmailId) {
                const found = shipperRequest.receivedQuotes.find((qr) => qr.id === quoteRequest.responseEmailId);
                if (found && found.status === "accepted") {
                    matchingResponse = found;
                    targetShipperRequest = shipperRequest;
                    break;
                }
            }

            // Se non trovato, prova matching tramite email del Freight Forwarder
            if (!matchingResponse && ffEmail) {
                const found = shipperRequest.receivedQuotes.find((qr) => {
                    return qr.from === ffEmail && qr.status === "accepted";
                });
                if (found) {
                    matchingResponse = found;
                    targetShipperRequest = shipperRequest;
                    break;
                }
            }

            // Se non trovato, prova matching tramite subject o body
            if (!matchingResponse) {
                const found = shipperRequest.receivedQuotes.find((qr) => {
                    // Match tramite subject
                    if (quoteRequest.responseSubject && qr.subject) {
                        return quoteRequest.responseSubject === qr.subject;
                    }
                    // Match tramite body (primi 100 caratteri)
                    if (quoteRequest.responseBody && qr.body) {
                        const qrBodyPreview = quoteRequest.responseBody.substring(0, 100).trim();
                        const responseBodyPreview = qr.body.substring(0, 100).trim();
                        return qrBodyPreview === responseBodyPreview;
                    }
                    return false;
                });

                if (found && found.status === "accepted") {
                    matchingResponse = found;
                    targetShipperRequest = shipperRequest;
                    break;
                }
            }
        }

        if (matchingResponse && targetShipperRequest) {
            // Aggiorna la QuoteResponse
            matchingResponse.bookingNumber = bookingNumber;
            targetShipperRequest.updatedAt = new Date();
            saveShipperQuoteRequests(); // Salva in localStorage

            console.log("[QuoteService] Booking number shared:", {
                quoteRequestId: targetShipperRequest.id,
                quoteResponseId: matchingResponse.id,
                bookingNumber: bookingNumber,
            });

            // Emetti evento per notificare che il booking number è stato condiviso
            if (typeof window !== "undefined") {
                window.dispatchEvent(
                    new CustomEvent("bookingNumberShared", {
                        detail: {
                            quoteRequestId: targetShipperRequest.id,
                            quoteResponseId: matchingResponse.id,
                            bookingNumber: bookingNumber,
                        },
                    }),
                );
            }
        } else {
            console.warn("[QuoteService] Could not find matching QuoteResponse for booking number:", {
                quoteRequestId,
                responseEmailId: quoteRequest.responseEmailId,
                responseSubject: quoteRequest.responseSubject,
                ffEmail,
            });
        }

        quoteRequest.lastUpdated = new Date();
        saveQuoteRequests(); // Salva in localStorage
        return quoteRequest;
    },

    // Condividi booking number con Shipper (già presente nella QuoteResponse, questo è per log)
    shareBookingNumberWithShipper: (quoteRequestId: string): QuoteRequest | null => {
        const quoteRequest = quoteRequests.find((qr) => qr.id === quoteRequestId);
        if (!quoteRequest) return null;

        // Il booking number è già presente nella QuoteResponse, quindi è già condiviso
        quoteRequest.lastUpdated = new Date();
        return quoteRequest;
    },

    // Sincronizza lo status shipper da una QuoteResponse a una QuoteRequest del FF
    // Il collegamento avviene tramite responseSubject/responseBody o email del FF
    syncShipperStatus: (quoteResponseId: string, status: "accepted" | "rejected" | "modification_requested", messagePreview?: string): QuoteRequest | null => {
        // Cerca la QuoteResponse in tutte le ShipperQuoteRequest
        let targetQuoteResponse: QuoteResponse | undefined;

        for (const shipperRequest of shipperQuoteRequests) {
            const found = shipperRequest.receivedQuotes.find((qr) => qr.id === quoteResponseId);
            if (found) {
                targetQuoteResponse = found;
                break;
            }
        }

        if (!targetQuoteResponse) return null;

        // Trova la QuoteRequest del FF che corrisponde a questa risposta
        // Il matching avviene tramite responseSubject o responseBody
        const matchingQuote = quoteRequests.find((qr) => {
            // Match tramite subject della risposta
            if (qr.responseSubject && targetQuoteResponse.subject) {
                return qr.responseSubject === targetQuoteResponse.subject;
            }
            // Match tramite body della risposta (primi 100 caratteri per evitare problemi di formattazione)
            if (qr.responseBody && targetQuoteResponse.body) {
                const qrBodyPreview = qr.responseBody.substring(0, 100).trim();
                const responseBodyPreview = targetQuoteResponse.body.substring(0, 100).trim();
                return qrBodyPreview === responseBodyPreview;
            }
            return false;
        });

        if (!matchingQuote) return null;

        // Aggiorna lo status shipper
        const index = quoteRequests.findIndex((qr) => qr.id === matchingQuote.id);
        if (index === -1) return null;

        quoteRequests[index] = {
            ...quoteRequests[index],
            shipperStatus: status,
            shipperMessagePreview: messagePreview || targetQuoteResponse.modificationRequestBody || undefined,
            lastUpdated: new Date(),
        };

        saveQuoteRequests(); // Salva in localStorage per persistenza

        // Emetti evento per notificare che lo status shipper è stato aggiornato
        if (typeof window !== "undefined") {
            window.dispatchEvent(
                new CustomEvent("shipperStatusUpdated", {
                    detail: { quoteRequestId: quoteRequests[index].id, status },
                }),
            );
        }

        return quoteRequests[index];
    },
};

// Quote Rule Service
export const quoteRuleService = {
    getAll: (): QuoteRule[] => {
        return [...quoteRules];
    },

    getById: (id: string): QuoteRule | undefined => {
        return quoteRules.find((rule) => rule.id === id);
    },

    create: (data: CreateQuoteRuleData): QuoteRule => {
        const newRule: QuoteRule = {
            id: `rule-${quoteRules.length + 1}`,
            ...data,
            isActive: data.isActive !== undefined ? data.isActive : true,
            createdAt: new Date(),
            updatedAt: new Date(),
        };
        quoteRules.push(newRule);
        return newRule;
    },

    update: (id: string, data: UpdateQuoteRuleData): QuoteRule | null => {
        const index = quoteRules.findIndex((rule) => rule.id === id);
        if (index === -1) return null;

        quoteRules[index] = {
            ...quoteRules[index],
            ...data,
            updatedAt: new Date(),
        };
        return quoteRules[index];
    },

    delete: (id: string): boolean => {
        const index = quoteRules.findIndex((rule) => rule.id === id);
        if (index === -1) return false;

        quoteRules.splice(index, 1);
        return true;
    },
};

// Pricing Settings Service
export const pricingSettingsService = {
    getAll: (): PricingSettings[] => {
        return [...pricingSettings];
    },

    getById: (id: string): PricingSettings | undefined => {
        return pricingSettings.find((settings) => settings.id === id);
    },

    getByRuleId: (ruleId: string): PricingSettings | undefined => {
        return pricingSettings.find((settings) => settings.ruleId === ruleId);
    },

    create: (data: CreatePricingSettingsData): PricingSettings => {
        const newSettings: PricingSettings = {
            id: `pricing-${pricingSettings.length + 1}`,
            ...data,
            sellRateComponents: data.sellRateComponents.map((comp, idx) => ({
                ...comp,
                id: `comp-${pricingSettings.length + 1}-${idx + 1}`,
            })),
            createdAt: new Date(),
            updatedAt: new Date(),
        };
        pricingSettings.push(newSettings);
        return newSettings;
    },

    update: (id: string, data: UpdatePricingSettingsData): PricingSettings | null => {
        const index = pricingSettings.findIndex((settings) => settings.id === id);
        if (index === -1) return null;

        const { sellRateComponents, ...restData } = data;
        const updateData: Partial<PricingSettings> = { ...restData };
        if (sellRateComponents) {
            updateData.sellRateComponents = sellRateComponents.map(
                (comp, idx): SellRateComponent => ({
                    ...comp,
                    id: (comp as any).id || `comp-${id}-${idx + 1}`,
                }),
            );
        }

        pricingSettings[index] = {
            ...pricingSettings[index],
            ...updateData,
            updatedAt: new Date(),
        };
        return pricingSettings[index];
    },

    delete: (id: string): boolean => {
        const index = pricingSettings.findIndex((settings) => settings.id === id);
        if (index === -1) return false;

        pricingSettings.splice(index, 1);
        return true;
    },
};

// Email Template Service
export const emailTemplateService = {
    getAll: (): EmailTemplate[] => {
        return [...emailTemplates];
    },

    getById: (id: string): EmailTemplate | undefined => {
        return emailTemplates.find((template) => template.id === id);
    },

    getByRuleId: (ruleId: string): EmailTemplate | undefined => {
        return emailTemplates.find((template) => template.ruleId === ruleId);
    },

    create: (data: CreateEmailTemplateData): EmailTemplate => {
        const newTemplate: EmailTemplate = {
            id: `template-${emailTemplates.length + 1}`,
            ...data,
            draftingInstructions: data.draftingInstructions.map((inst, idx) => ({
                ...inst,
                id: `inst-${emailTemplates.length + 1}-${idx + 1}`,
            })),
            createdAt: new Date(),
            updatedAt: new Date(),
        };
        emailTemplates.push(newTemplate);
        return newTemplate;
    },

    update: (id: string, data: UpdateEmailTemplateData): EmailTemplate | null => {
        const index = emailTemplates.findIndex((template) => template.id === id);
        if (index === -1) return null;

        const { draftingInstructions, ...restData } = data;
        const updateData: Partial<EmailTemplate> = { ...restData };
        if (draftingInstructions) {
            updateData.draftingInstructions = draftingInstructions.map(
                (inst, idx): DraftingInstruction => ({
                    ...inst,
                    id: (inst as any).id || `inst-${id}-${idx + 1}`,
                }),
            );
        }

        emailTemplates[index] = {
            ...emailTemplates[index],
            ...updateData,
            updatedAt: new Date(),
        };
        return emailTemplates[index];
    },

    delete: (id: string): boolean => {
        const index = emailTemplates.findIndex((template) => template.id === id);
        if (index === -1) return false;

        emailTemplates.splice(index, 1);
        return true;
    },
};

// Mock data per ShipperQuoteRequest
const mockShipperQuoteRequests: ShipperQuoteRequest[] = [
    {
        id: "sqr-1",
        subject: "Richiesta preventivo trasporto avocado da Lima a Milano",
        body: `Gentile fornitore,

siamo interessati a ricevere un preventivo per il trasporto di 500 kg di avocado dal Perù a Milano.

Richiediamo:
- Ritiro: Lima, Perù - 15 marzo 2024
- Consegna: Milano, Italia - entro il 25 marzo 2024
- Temperatura controllata: 4-8°C
- Certificazioni: necessarie per importazione UE

Restiamo in attesa di un vostro preventivo dettagliato.

Cordiali saluti,
TechCorp Solutions LLC`,
        sentAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000),
        status: "responded",
        recipients: ["2"], // Address book entry ID per freight forwarder
        extractedData: {
            product: "avocado",
            origin: "Lima, Perù",
            destination: "Milano, Italia",
            quantity: 500,
            unit: "kg",
            pickupDate: new Date("2024-03-15"),
            deliveryDate: new Date("2024-03-25"),
            temperatureRequirements: "4-8°C",
            customsRequirements: "Certificazione importazione UE",
        },
        receivedQuotes: [
            {
                id: "qr-1",
                from: "giulia.bianchi@freightforwarder.com",
                fromName: "Giulia Bianchi",
                subject: "Preventivo trasporto avocado - QUOTE-001",
                body: `Gentile TechCorp Solutions,

grazie per la sua richiesta di preventivo per il trasporto di avocado.

Siamo lieti di fornirle il seguente preventivo:

**Dettagli Trasporto:**
- Origine: Lima, Perù
- Destinazione: Milano, Italia
- Quantità: 500 kg
- Data ritiro: 15 marzo 2024
- Data consegna prevista: 25 marzo 2024

**Preventivo:**
- Costo base: 2.500 EUR
- Componenti aggiuntive: 500 EUR
- **Totale: 3.000 EUR**

Il preventivo è valido per 30 giorni.

Restiamo a disposizione per qualsiasi chiarimento.

Cordiali saluti,
Giulia Bianchi
Global Logistics Forwarding`,
                receivedAt: new Date(now.getTime() - 4 * 24 * 60 * 60 * 1000),
                quoteRequestId: "sqr-1",
                price: 3000,
                currency: "EUR",
                validity: 30,
                terms: "Preventivo valido per 30 giorni. Pagamento anticipato richiesto.",
                isRead: false,
            },
        ],
        shipperId: "1",
        createdAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 4 * 24 * 60 * 60 * 1000),
    },
    {
        id: "sqr-2",
        subject: "Richiesta preventivo trasporto container elettronica",
        body: `Salve,

cerchiamo un preventivo per trasporto container da Shanghai a Genova.

Dettagli:
- Tipo: Container 40 piedi
- Merce: Elettronica
- Ritiro: 10 aprile 2024
- Consegna: 15 maggio 2024
- Valore merce: €50.000

Richiediamo anche assicurazione completa.

Cordiali saluti,
TechCorp Solutions LLC`,
        sentAt: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000),
        status: "pending",
        recipients: ["2"],
        extractedData: {
            product: "elettronica",
            origin: "Shanghai, Cina",
            destination: "Genova, Italia",
            quantity: 1,
            unit: "container 40 piedi",
            pickupDate: new Date("2024-04-10"),
            deliveryDate: new Date("2024-05-15"),
            estimatedValue: 50000,
            currency: "EUR",
            equipmentType: "Container 40 piedi",
            specialRequirements: ["Assicurazione completa"],
        },
        receivedQuotes: [],
        shipperId: "1",
        createdAt: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000),
        updatedAt: new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000),
    },
];

// Helper per serializzare/deserializzare Date nelle ShipperQuoteRequest
function serializeShipperQuoteRequest(request: ShipperQuoteRequest): any {
    return {
        ...request,
        sentAt: request.sentAt.toISOString(),
        createdAt: request.createdAt.toISOString(),
        updatedAt: request.updatedAt.toISOString(),
        receivedQuotes: request.receivedQuotes.map((qr) => ({
            ...qr,
            receivedAt: qr.receivedAt.toISOString(),
            extractedData: qr.extractedData
                ? {
                      ...qr.extractedData,
                      pickupDate: qr.extractedData.pickupDate?.toISOString(),
                      deliveryDate: qr.extractedData.deliveryDate?.toISOString(),
                  }
                : undefined,
        })),
        extractedData: request.extractedData
            ? {
                  ...request.extractedData,
                  pickupDate: request.extractedData.pickupDate?.toISOString(),
                  deliveryDate: request.extractedData.deliveryDate?.toISOString(),
              }
            : undefined,
    };
}

function deserializeShipperQuoteRequest(request: any): ShipperQuoteRequest {
    return {
        ...request,
        sentAt: new Date(request.sentAt),
        createdAt: new Date(request.createdAt),
        updatedAt: new Date(request.updatedAt),
        receivedQuotes: request.receivedQuotes.map((qr: any) => ({
            ...qr,
            receivedAt: new Date(qr.receivedAt),
            extractedData: qr.extractedData
                ? {
                      ...qr.extractedData,
                      pickupDate: qr.extractedData.pickupDate ? new Date(qr.extractedData.pickupDate) : undefined,
                      deliveryDate: qr.extractedData.deliveryDate ? new Date(qr.extractedData.deliveryDate) : undefined,
                  }
                : undefined,
        })),
        extractedData: request.extractedData
            ? {
                  ...request.extractedData,
                  pickupDate: request.extractedData.pickupDate ? new Date(request.extractedData.pickupDate) : undefined,
                  deliveryDate: request.extractedData.deliveryDate ? new Date(request.extractedData.deliveryDate) : undefined,
              }
            : undefined,
    };
}

// Carica shipper quote requests da localStorage o usa i mock iniziali
function loadShipperQuoteRequests(): ShipperQuoteRequest[] {
    if (typeof window === "undefined") {
        return [...mockShipperQuoteRequests];
    }

    try {
        const stored = storage.getItem(LOCAL_STORAGE_KEY_SHIPPER_QUOTE_REQUESTS);
        if (stored) {
            const parsed = JSON.parse(stored);
            const storedRequests = parsed.map(deserializeShipperQuoteRequest);
            const mockIds = new Set(mockShipperQuoteRequests.map((r) => r.id));
            const newStoredRequests = storedRequests.filter((r: ShipperQuoteRequest) => !mockIds.has(r.id));
            return [...mockShipperQuoteRequests, ...newStoredRequests];
        }
    } catch (error) {
        console.error("Error loading shipper quote requests from localStorage:", error);
    }

    return [...mockShipperQuoteRequests];
}

// Salva shipper quote requests in localStorage
function saveShipperQuoteRequests(): void {
    if (typeof window === "undefined") return;
    try {
        storage.setItem(LOCAL_STORAGE_KEY_SHIPPER_QUOTE_REQUESTS, JSON.stringify(shipperQuoteRequests.map(serializeShipperQuoteRequest)));
    } catch (error) {
        console.error("Error saving shipper quote requests to localStorage:", error);
    }
}

// In-memory storage per ShipperQuoteRequest
// Dichiarato prima di quoteService per permettere l'accesso in syncShipperStatus
export let shipperQuoteRequests = loadShipperQuoteRequests();
let quoteResponses: QuoteResponse[] = [];

// Estrai tutte le quote responses dai shipperQuoteRequests
shipperQuoteRequests.forEach((request) => {
    quoteResponses.push(...request.receivedQuotes);
});

// Helper function per generare quote response mock
export const generateMockQuoteResponse = (quoteRequestId: string, recipientId: string): QuoteResponse => {
    const request = shipperQuoteRequests.find((req) => req.id === quoteRequestId);
    if (!request) {
        throw new Error(`Quote request ${quoteRequestId} not found`);
    }

    const recipient = addressBookService.getById(recipientId);

    if (!recipient) {
        throw new Error(`Recipient ${recipientId} not found`);
    }

    const extractedData = request.extractedData;
    const basePrice = extractedData?.estimatedValue ? extractedData.estimatedValue * 0.15 : 2000; // 15% del valore stimato o 2000 EUR
    const randomVariation = 0.8 + Math.random() * 0.4; // Variazione tra 80% e 120%
    const price = Math.round(basePrice * randomVariation);

    const productName = extractedData?.product || "merce";
    const origin = extractedData?.origin || "Origine";
    const destination = extractedData?.destination || "Destinazione";
    const quantity = extractedData?.quantity || 1;
    const unit = extractedData?.unit || "unità";

    const quoteNumber = `QUOTE-${Date.now().toString().slice(-6)}`;
    const subject = `Preventivo trasporto ${productName} - ${quoteNumber}`;

    const body = `Gentile cliente,

grazie per la sua richiesta di preventivo per il trasporto di ${productName}.

Siamo lieti di fornirle il seguente preventivo:

**Dettagli Trasporto:**
- Origine: ${origin}
- Destinazione: ${destination}
- Quantità: ${quantity} ${unit}
${extractedData?.pickupDate ? `- Data ritiro: ${extractedData.pickupDate.toLocaleDateString("it-IT")}` : ""}
${extractedData?.deliveryDate ? `- Data consegna prevista: ${extractedData.deliveryDate.toLocaleDateString("it-IT")}` : ""}
${extractedData?.temperatureRequirements ? `- Temperatura controllata: ${extractedData.temperatureRequirements}` : ""}

**Preventivo:**
- Costo base: ${Math.round(price * 0.8).toLocaleString("it-IT")} EUR
- Componenti aggiuntive: ${Math.round(price * 0.2).toLocaleString("it-IT")} EUR
- **Totale: ${price.toLocaleString("it-IT")} EUR**

Il preventivo è valido per 30 giorni.

Restiamo a disposizione per qualsiasi chiarimento.

Cordiali saluti,
${recipient.firstName} ${recipient.lastName}
${recipient.email}`;

    const quoteResponse: QuoteResponse = {
        id: `qr-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        from: recipient.email,
        fromName: `${recipient.firstName} ${recipient.lastName}`,
        subject,
        body,
        receivedAt: new Date(),
        quoteRequestId,
        price,
        currency: extractedData?.currency || "EUR",
        validity: 30,
        terms: "Preventivo valido per 30 giorni. Pagamento anticipato richiesto.",
        extractedData: extractedData ? { ...extractedData } : undefined,
        isRead: false,
        status: "pending",
    };

    return quoteResponse;
};

// Shipper Quote Request Service
export const shipperQuoteService = {
    // Get all quote requests for a shipper
    getAll: (shipperId: string): ShipperQuoteRequest[] => {
        return shipperQuoteRequests.filter((req) => req.shipperId === shipperId);
    },

    // Get quote request by ID
    getById: (id: string): ShipperQuoteRequest | undefined => {
        return shipperQuoteRequests.find((req) => req.id === id);
    },

    // Create new quote request
    create: (data: CreateShipperQuoteRequestData): ShipperQuoteRequest => {
        const newRequest: ShipperQuoteRequest = {
            id: `sqr-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            subject: data.subject,
            body: data.body,
            sentAt: new Date(),
            status: "pending",
            recipients: data.recipients,
            extractedData: data.extractedData,
            receivedQuotes: [],
            shipperId: data.shipperId,
            orderId: data.orderId,
            createdAt: new Date(),
            updatedAt: new Date(),
        };
        shipperQuoteRequests.push(newRequest);
        saveShipperQuoteRequests(); // Salva in localStorage

        // Crea anche una QuoteRequest per ogni Freight Forwarder destinatario
        // così che possano vederla nella loro lista
        const shipperAccount = accountService.getById(data.shipperId);

        if (shipperAccount && data.recipients && data.recipients.length > 0) {
            // Raggruppa i recipient per email del Freight Forwarder per evitare duplicati
            const ffEmailMap = new Map<string, { addressBookEntryId: string; addressBookEntry: any; ffAccount: any }>();

            data.recipients.forEach((addressBookEntryId) => {
                // Trova l'address book entry per ottenere informazioni sul Freight Forwarder
                const addressBookEntry = addressBookService.getById(addressBookEntryId);

                if (!addressBookEntry) {
                    console.warn(`[QuoteService] Address book entry ${addressBookEntryId} not found`);
                    return;
                }

                // Prova a trovare l'account del Freight Forwarder usando l'email dall'address book
                const allAccounts = accountService.getAll();
                const freightForwarderAccount = allAccounts.find((acc) => acc.email === addressBookEntry.email && acc.userType === "FREIGHT_FORWARDER");

                // Usa l'email come chiave per raggruppare (evita duplicati per lo stesso FF)
                const ffEmail = addressBookEntry.email;
                if (!ffEmailMap.has(ffEmail)) {
                    ffEmailMap.set(ffEmail, {
                        addressBookEntryId,
                        addressBookEntry,
                        ffAccount: freightForwarderAccount,
                    });
                }
            });

            // Crea una sola QuoteRequest per ogni Freight Forwarder unico
            ffEmailMap.forEach(({ addressBookEntry, ffAccount }, ffEmail) => {
                // Verifica se esiste già una QuoteRequest per questo Freight Forwarder con lo stesso subject e from
                const existingQuote = quoteRequests.find(
                    (q) =>
                        q.from === shipperAccount.email &&
                        q.subject === data.subject &&
                        // Controlla se è per lo stesso FF (confrontando l'email nel fromName o cercando nel subject/body)
                        q.receivedAt &&
                        Math.abs(q.receivedAt.getTime() - new Date().getTime()) < 60000, // Creato negli ultimi 60 secondi
                );

                if (existingQuote) {
                    console.log(`[QuoteService] QuoteRequest already exists for ${ffEmail}, skipping duplicate`);
                    return;
                }

                const timestamp = Date.now();
                const randomSuffix = Math.random().toString(36).substr(2, 9);
                // Usa l'ID del Freight Forwarder account se trovato, altrimenti usa l'email
                const ffId = ffAccount?.id || ffEmail.replace(/[@.]/g, "-");
                const quoteRequestId = `qr-${timestamp}-${randomSuffix}-${ffId}`;
                const quoteRequest: QuoteRequest = {
                    id: quoteRequestId,
                    from: shipperAccount.email,
                    fromName: shipperAccount.legalName,
                    subject: data.subject,
                    body: data.body,
                    receivedAt: new Date(),
                    status: "pending",
                    extractedData: data.extractedData,
                    lastUpdated: new Date(),
                };

                quoteRequests.push(quoteRequest);
                saveQuoteRequests(); // Salva in localStorage
                const ffInfo = ffAccount
                    ? `${ffAccount.legalName} (${ffAccount.email})`
                    : `${addressBookEntry.firstName} ${addressBookEntry.lastName} (${addressBookEntry.email})`;
                console.log(`[QuoteService] Created QuoteRequest ${quoteRequestId} from ${shipperAccount.legalName} for ${ffInfo}`);
                console.log(`[QuoteService] Total QuoteRequests now: ${quoteRequests.length}`);

                // Emetti evento per notificare che è stata creata una nuova quote request
                if (typeof window !== "undefined") {
                    window.dispatchEvent(new Event("newQuoteRequestCreated"));
                    console.log(`[QuoteService] Dispatched newQuoteRequestCreated event`);
                }
            });
        } else {
            console.warn(`[QuoteService] Cannot create QuoteRequests: shipperAccount=${!!shipperAccount}, recipients=${data.recipients?.length || 0}`);
        }

        return newRequest;
    },

    // Update quote request
    update: (id: string, data: UpdateShipperQuoteRequestData): ShipperQuoteRequest | null => {
        const index = shipperQuoteRequests.findIndex((req) => req.id === id);
        if (index === -1) return null;

        shipperQuoteRequests[index] = {
            ...shipperQuoteRequests[index],
            ...data,
            updatedAt: new Date(),
        };
        saveShipperQuoteRequests(); // Salva in localStorage
        return shipperQuoteRequests[index];
    },

    // Delete quote request
    delete: (id: string): boolean => {
        const index = shipperQuoteRequests.findIndex((req) => req.id === id);
        if (index === -1) return false;

        // Rimuovi anche le quote responses associate
        quoteResponses = quoteResponses.filter((qr) => qr.quoteRequestId !== id);

        shipperQuoteRequests.splice(index, 1);
        saveShipperQuoteRequests(); // Salva in localStorage
        return true;
    },

    // Add quote response to a request
    addQuoteResponse: (quoteRequestId: string, response: QuoteResponse): boolean => {
        const request = shipperQuoteRequests.find((req) => req.id === quoteRequestId);
        if (!request) return false;

        request.receivedQuotes.push(response);
        quoteResponses.push(response);

        // Aggiorna status se è la prima risposta
        if (request.status === "pending" && request.receivedQuotes.length === 1) {
            request.status = "responded";
        }

        request.updatedAt = new Date();
        saveShipperQuoteRequests(); // Salva in localStorage
        return true;
    },

    // Select a quote and mark request as completed
    selectQuote: (quoteRequestId: string, quoteResponseId: string): boolean => {
        const request = shipperQuoteRequests.find((req) => req.id === quoteRequestId);
        if (!request) return false;

        const quoteExists = request.receivedQuotes.some((qr) => qr.id === quoteResponseId);
        if (!quoteExists) return false;

        request.selectedQuoteId = quoteResponseId;
        request.status = "completed";
        request.updatedAt = new Date();
        saveShipperQuoteRequests(); // Salva in localStorage
        return true;
    },

    // Cancel a quote request
    cancel: (id: string): boolean => {
        const request = shipperQuoteRequests.find((req) => req.id === id);
        if (!request) return false;

        if (request.status === "completed") return false; // Non si può cancellare se completata

        request.status = "cancelled";
        request.updatedAt = new Date();
        saveShipperQuoteRequests(); // Salva in localStorage
        return true;
    },

    // Mark a quote response as read
    markQuoteAsRead: (requestId: string, quoteResponseId: string): ShipperQuoteRequest | null => {
        const request = shipperQuoteRequests.find((req) => req.id === requestId);
        if (!request) return null;

        const quoteResponse = request.receivedQuotes.find((qr) => qr.id === quoteResponseId);
        if (quoteResponse) {
            quoteResponse.isRead = true;
            request.updatedAt = new Date();
            saveShipperQuoteRequests(); // Salva in localStorage
            return request;
        }

        return null;
    },

    // Update quote response status
    updateQuoteResponseStatus: (
        requestId: string,
        quoteResponseId: string,
        status: "pending" | "accepted" | "rejected" | "modification_requested",
    ): ShipperQuoteRequest | null => {
        const request = shipperQuoteRequests.find((req) => req.id === requestId);
        if (!request) return null;

        const quoteResponse = request.receivedQuotes.find((qr) => qr.id === quoteResponseId);
        if (quoteResponse) {
            quoteResponse.status = status;
            request.updatedAt = new Date();

            // Sincronizza con QuoteRequest del freight forwarder se lo status non è pending
            if (status !== "pending") {
                quoteService.syncShipperStatus(quoteResponseId, status);
            }

            saveShipperQuoteRequests(); // Salva in localStorage
            return request;
        }

        return null;
    },

    // Salva il corpo della richiesta modifica inviata dallo shipper
    saveModificationRequestBody: (requestId: string, quoteResponseId: string, body: string, _subject?: string): ShipperQuoteRequest | null => {
        const request = shipperQuoteRequests.find((req) => req.id === requestId);
        if (!request) return null;

        const quoteResponse = request.receivedQuotes.find((qr) => qr.id === quoteResponseId);
        if (!quoteResponse) return null;

        quoteResponse.modificationRequestBody = body;
        quoteResponse.status = "modification_requested";
        request.updatedAt = new Date();

        // Sincronizza con QuoteRequest del freight forwarder
        quoteService.syncShipperStatus(quoteResponseId, "modification_requested", body);

        saveShipperQuoteRequests(); // Salva in localStorage
        return request;
    },

    // Salva il corpo della controproposta del freight forwarder
    saveModificationResponseBody: (requestId: string, quoteResponseId: string, body: string): ShipperQuoteRequest | null => {
        const request = shipperQuoteRequests.find((req) => req.id === requestId);
        if (!request) return null;

        const quoteResponse = request.receivedQuotes.find((qr) => qr.id === quoteResponseId);
        if (!quoteResponse) return null;

        quoteResponse.modificationResponseBody = body;
        request.updatedAt = new Date();

        saveShipperQuoteRequests(); // Salva in localStorage
        return request;
    },

    // Crea una quote aggiornata dopo richiesta modifica (nuova entry nella lista)
    createUpdatedQuoteAfterModification: (
        requestId: string,
        originalQuoteResponseId: string,
        updatedBody: string,
        updatedPrice?: number,
        updatedSubject?: string,
        updatedValidity?: number,
        updatedTerms?: string,
    ): ShipperQuoteRequest | null => {
        const request = shipperQuoteRequests.find((req) => req.id === requestId);
        if (!request) return null;

        const originalQuote = request.receivedQuotes.find((qr) => qr.id === originalQuoteResponseId);
        if (!originalQuote || originalQuote.status !== "modification_requested") return null;

        // Aggiorna la quote esistente invece di crearne una nuova
        const updatedPriceValue = updatedPrice !== undefined ? updatedPrice : originalQuote.price;
        const updatedSubjectValue = updatedSubject || originalQuote.subject;
        const updatedValidityValue = updatedValidity !== undefined ? updatedValidity : originalQuote.validity;
        const updatedTermsValue = updatedTerms || originalQuote.terms;

        // Aggiorna la quote esistente
        originalQuote.subject = updatedSubjectValue;
        originalQuote.body = updatedBody;
        originalQuote.price = updatedPriceValue;
        if (updatedValidityValue !== undefined) {
            originalQuote.validity = updatedValidityValue;
        }
        if (updatedTermsValue) {
            originalQuote.terms = updatedTermsValue;
        }
        originalQuote.modificationResponseBody = updatedBody; // Salva la risposta alla modifica
        originalQuote.status = "pending"; // Reimposta a pending per permettere allo Shipper di accettare/rifiutare
        originalQuote.receivedAt = new Date(); // Aggiorna la data di ricezione
        originalQuote.isRead = false; // Marca come non letta

        // Aggiorna status della richiesta se necessario
        if (request.status === "responded") {
            // Mantieni responded se ci sono già altre quote
        } else if (request.status === "pending") {
            request.status = "responded";
        }

        request.updatedAt = new Date();
        saveShipperQuoteRequests(); // Salva in localStorage

        return request;
    },

    // Richiedi booking number per una quote accettata
    requestBookingNumber: (requestId: string, quoteResponseId: string): ShipperQuoteRequest | null => {
        const request = shipperQuoteRequests.find((req) => req.id === requestId);
        if (!request) return null;

        const quoteResponse = request.receivedQuotes.find((qr) => qr.id === quoteResponseId);
        if (!quoteResponse || quoteResponse.status !== "accepted") return null;

        quoteResponse.bookingNumberRequested = true;
        quoteResponse.bookingNumberRequestedAt = new Date();
        request.updatedAt = new Date();

        return request;
    },

    // Imposta booking number per una quote (può essere inserito manualmente o simulato)
    setBookingNumber: (requestId: string, quoteResponseId: string, bookingNumber: string): ShipperQuoteRequest | null => {
        const request = shipperQuoteRequests.find((req) => req.id === requestId);
        if (!request) return null;

        const quoteResponse = request.receivedQuotes.find((qr) => qr.id === quoteResponseId);
        if (!quoteResponse || quoteResponse.status !== "accepted") return null;

        quoteResponse.bookingNumber = bookingNumber;
        request.updatedAt = new Date();

        return request;
    },

    // Condividi booking number con lo Shipper (simula la comunicazione)
    shareBookingNumberWithShipper: (requestId: string, quoteResponseId: string): ShipperQuoteRequest | null => {
        const request = shipperQuoteRequests.find((req) => req.id === requestId);
        if (!request) return null;

        const quoteResponse = request.receivedQuotes.find((qr) => qr.id === quoteResponseId);
        if (!quoteResponse || !quoteResponse.bookingNumber) return null;

        // Il booking number è già presente nella quote response, quindi è già condiviso
        // Questo metodo può essere usato per log o notifiche future
        request.updatedAt = new Date();

        return request;
    },

    // Completa la richiesta e crea container sulla base delle quote accepted
    completeRequestAndSimulateContainers: (requestId: string): { request: ShipperQuoteRequest | null; containerId: string | null } => {
        const request = shipperQuoteRequests.find((req) => req.id === requestId);
        if (!request) return { request: null, containerId: null };

        const acceptedQuotes = request.receivedQuotes.filter((qr) => qr.status === "accepted");
        if (acceptedQuotes.length === 0) {
            return { request, containerId: null };
        }

        // Crea container con info aggregate dalle quote accettate
        const container = containerService.createFromQuoteRequest(requestId, request.orderId);

        if (container) {
            request.status = "completed";
            request.updatedAt = new Date();
            saveShipperQuoteRequests(); // Salva in localStorage
            return { request, containerId: container.id };
        }

        return { request, containerId: null };
    },
};

// Quote Response Service
export const quoteResponseService = {
    // Get all responses for a quote request
    getByQuoteRequestId: (quoteRequestId: string): QuoteResponse[] => {
        return quoteResponses.filter((qr) => qr.quoteRequestId === quoteRequestId);
    },

    // Get response by ID
    getById: (id: string): QuoteResponse | undefined => {
        return quoteResponses.find((qr) => qr.id === id);
    },
};
